import Account from "../Account";
import ApiHttp from "../ApiHttp";
import "../types/models/v1/Limits.types";

export type Operation = "read" | "write" | "*";

export const ALL = "*";

export const MODEL_STR = {
  ACCOUNT: "indigo.Account",
  ACH_TRANSFER: "indigo.AchTransfer",
  ACH_PAYMENT_VIA_TEMPLATE: "indigo.ACHPaymentViaTemplate",
  ACH_PAYMENT_COLLECT_VIA_TEMPLATE: "indigo.ACHPaymentCollectViaTemplate",
  ACH_PAYMENT_VIA_IMPORT: "indigo.ACHPaymentViaImport",
  ACH_PAYMENT_COLLECT_VIA_IMPORT: "indigo.ACHPaymentCollectViaImport",
  BULK_PAYMENT_TEMPLATE: "indigo.BulkPaymentTemplateGroup",
  ALL_MODELS: ALL,
  CARD: "integrations.base.Card",
  EXTERNAL_ACCOUNT: "indigo.ExternalAccount",
  INTRA_INSTITUTION_TRANSFER: "indigo.IntraInstitutionTransfer",
  MEMBER_FRIEND: "indigo.MemberFriend",
  PAYEE: "integrations.base.Payee",
  PAYMENT: "integrations.base.Payment",
  TRANSACTION: "indigo.Transaction",
  /** @deprecated Use DOMESTIC_WIRE_TRANSACTION instead */
  WIRE_TRANSACTION: "indigo.WireTransaction",
  WIRE_TEMPLATES: "indigo.FedwireTemplate",
  WIRE_VIA_TEMPLATES: "indigo.FedwireViaTemplate",
  DOMESTIC_WIRE_TRANSACTION: "indigo.WireTransaction",
  DOMESTIC_WIRE_TEMPLATES: "indigo.FedwireTemplate",
  DOMESTIC_WIRE_VIA_TEMPLATES: "indigo.FedwireViaTemplate",
  DOCUMENTS: "integrations.documents",
  RDC: "integrations.rdc",
  DUAL_APPROVAL_REQUEST: "indigo.DualApprovalRequest",
  ORGANIZATION: "indigo.Organization",
  ORGANIZATION_USER: "indigo.OrganizationUser",
  ROLE: "indigo.Role",
  RECIPIENT: "indigo.Recipient",
  ACH_PAYMENT: "indigo.ACHPayment",
  ACH_IMPORT_FILE_DOWNLOADS: "indigo.ACHImportFileDownloads",
  ZELLE: "integrations.Zelle",
  INTERNATIONAL_WIRE: "indigo.SwiftWireTransaction",
  INTERNATIONAL_WIRE_TEMPLATES: "indigo.SwiftWireTemplate",
  INTERNATIONAL_WIRE_VIA_TEMPLATES: "indigo.SwiftWireViaTemplate",
  ACH_PAYMENT_COLLECT: "indigo.ACHPaymentCollect",
  COLLECT_ACH_PAYMENT: "indigo.ACHPaymentCollect",
  ACH_PAYMENT_REVERSE: "indigo.ACHPaymentReverse",
  ACH_PAYMENT_COLLECT_REVERSE: "indigo.ACHPaymentCollectReverse",
  UNVERIFIED_FEDNOW_SEND: "indigo.UnverifiedFedNowSend",
  APPROVE_ACH_TEMPLATES: "indigo.ApproveACHTemplates",
  DOMESTIC_WIRE_IMPORT: "indigo.DomesticWireImport",
  BANKING_API_TOKEN_ALLOWED_SCOPES: "indigo.BankingAccessTokenAllowedScopes",
  ACH_PASSTHROUGH_SEND: "indigo.ACHPassthroughSend",
  ACH_PASSTHROUGH_COLLECT: "indigo.ACHPassthroughCollect",
};

export type ModelStr = (typeof MODEL_STR)[keyof typeof MODEL_STR];

const ACCOUNT_HOLDER_ROLE = "Account Holder";
const KNOWN_SYSTEM_DEFAULT_ROLES = [
  ACCOUNT_HOLDER_ROLE,
  "Admin",
  "Viewer",
  "No Permissions",
];

export class Role {
  public uuid: string | undefined;

  public organization_uuid: string | undefined;

  public name: string;

  public description: string | undefined;

  public permissions: API.Permission[];

  public limits: API.Limits.ByType;

  public deleted_at: string | null | undefined;

  /**
  Only populated from the role detail endpoint, never from the list.
  `undefined`: not fetched.
  `null`: unconverted custom role (use `permissions`).
  `[]`: converted role with no accessible accounts.
  * */
  public account_permissions: API.AccountPermission[] | null | undefined;

  /** Only populated from the role detail endpoint. `null`: unconverted custom role. */
  public accounts_opened_later: API.AccountsOpenedLater | null | undefined;

  public account_permission_catalog: API.AccountPermissionCatalogEntry[];

  public capability_grants_saved_at: string | null | undefined;

  constructor(props: {
    uuid?: string;
    organization_uuid?: string;
    name: string;
    description?: string;
    permissions?: API.Permission[];
    limits?: API.Limits.ByType;
    deleted_at?: string | null;
    account_permissions?: API.AccountPermission[] | null;
    accounts_opened_later?: API.AccountsOpenedLater | null;
    account_permission_catalog?: API.AccountPermissionCatalogEntry[];
    capability_grants_saved_at?: string | null;
  }) {
    this.uuid = props.uuid;
    this.organization_uuid = props.organization_uuid;
    this.name = props.name;
    this.description = props.description;
    this.permissions = props.permissions || [];
    this.limits = props.limits || {};
    this.deleted_at = props.deleted_at;
    this.account_permissions = props.account_permissions;
    this.accounts_opened_later = props.accounts_opened_later;
    this.account_permission_catalog = props.account_permission_catalog || [];
    this.capability_grants_saved_at = props.capability_grants_saved_at;
  }

  static forOrganization(orgUuid: UUID, method = "GET") {
    return ApiHttp.fetch(`organizations/${orgUuid}/roles`, {
      method: method as "GET" | "POST" | "PUT" | "DELETE",
    }).then((res) => {
      return res.roles.map((r: API.Role) => Role.deserialize(r));
    });
  }

  isAccountHolder() {
    return this.name === ACCOUNT_HOLDER_ROLE;
  }

  isNoPermissions() {
    return this.name === "No Permissions";
  }

  isKnownDefaultRole() {
    return KNOWN_SYSTEM_DEFAULT_ROLES.includes(this.name);
  }

  /**
  If Account Holder, return "Account owner"
  Otherwise returns "Admin", "Collaborator", or "Viewer"
  Necessary only for the Organization Users table
  * */
  getAccessLevelUnlessIsOwner() {
    if (this.name === "Account Holder") {
      return "Account owner";
    }
    return this.getAccessLevel();
  }

  /**
  Returns "Admin", "Collaborator", or "Viewer"
  * */
  getAccessLevel() {
    if (this.name === "Admin" || this.name === "Account Holder") {
      return "Admin";
    }

    if (
      this.permissions.filter(
        (p) =>
          p.model_str === MODEL_STR.ACCOUNT &&
          [ALL, "write"].includes(p.operation),
      ).length
    ) {
      return "Collaborator";
    }
    return "Viewer";
  }

  /**
  Returns true if this role was created without a name.
  Roles created without a name are identified by having "(Unassigned)".
  * */
  isUnassigned() {
    return this.name.includes("(Unassigned)");
  }

  /**
  Usually just the role.name.
  We hide the real role name if is unassigned.
  * */
  getDisplayName() {
    if (this.isAccountHolder()) {
      return "Account owner";
    }
    if (this.isUnassigned()) {
      return `${this.getAccessLevel()} (Unassigned)`;
    }
    return this.name;
  }

  /**
  Returns array of accounts with added prop "selected" that is truthy if there
  is a corresponding permission
  * */
  markSelectedAccounts(accounts: API.Account[]) {
    return accounts.map((account) => {
      const markedAccount = new Account(account as Partial<any>);
      // if we have a permission for this account, mark as selected
      const relevantPermission = markedAccount.isInternal()
        ? MODEL_STR.ACCOUNT
        : MODEL_STR.EXTERNAL_ACCOUNT;
      (markedAccount as any).selected = this.permissions.find(
        (p) =>
          relevantPermission === p.model_str &&
          [ALL, String(account.id)].includes(String(p.uuid)),
      );
      return markedAccount;
    });
  }

  /**
  Factory method generating a canonical Role from AccessLevel format
  Deprecated, please create roles directly, and not from access level.
  * */
  static generateFromAccessLevelDeprecated(
    accessLevel: "Admin" | "Collaborator" | "Viewer",
    accounts: API.Account[],
    externalAccounts: API.ExternalAccount[],
    permissionOverrides: Record<string, boolean>,
    roles: Role[],
    limits: API.Limits.ByType,
  ) {
    // Look up our role if we have one
    const defaultRole = roles.find((r) => r.name === accessLevel);
    const OPERATION_FOR_ACCESS_LEVEL = {
      Collaborator: "write",
      Viewer: "read",
    };
    // special handling for specific AccessLevels
    const mutateAccountOperation = (OPERATION_FOR_ACCESS_LEVEL as any)[
      accessLevel
    ];
    // Collaborator and Viewer have special handling, otherwise we have our role
    if (!mutateAccountOperation) {
      return defaultRole || Role.deserialize({ name: accessLevel });
    }

    // make a new custom role
    const role = Role.deserialize({ name: `Custom ${accessLevel} role` });

    // apply permissionOverrides
    if (permissionOverrides) {
      Object.keys(permissionOverrides).forEach((key) => {
        if (permissionOverrides[key]) {
          // add the new permission if we need to
          if (role.permissions.find((p) => p.model_str === key)) return;
          role.permissions.push({
            model_str: key,
            operation: mutateAccountOperation,
            uuid: ALL,
          });
        } else {
          // entering this block means that the permission override has been disabled,
          // so we want to make sure to remove any related permissions
          role.permissions = role.permissions.filter(
            (p) => p.model_str !== key,
          );
        }
      });
    }
    // update the account permissions for our new custom role
    accounts.forEach((account) => {
      if (!(account as any).selected) return;
      role.permissions.push({
        model_str: MODEL_STR.ACCOUNT,
        operation: mutateAccountOperation,
        uuid: account.id,
      });
    });
    // the permissions for individual external accounts are different
    // from the permission to add, edit, or remove any external account
    // if the "Add external account" permission is removed, we still want to keep any individual external account permissions
    externalAccounts.forEach((externalAccount) => {
      if (!(externalAccount as any).selected) return;
      if (!role.permissions.some((p) => p.model_str === MODEL_STR.ACH_TRANSFER))
        return;
      role.permissions.push({
        model_str: MODEL_STR.EXTERNAL_ACCOUNT,
        operation: mutateAccountOperation,
        uuid: externalAccount.id,
      });
    });

    // apply daily limits
    if (limits) {
      const limitsAny = limits as any;
      let {
        ach_limits,
        ach_payment_limits,
        collect_ach_payment_limits,
        international_wire_limits,
        fednow_unverified_send_limits,
        ach_passthrough_send_limits,
        ach_passthrough_collect_limits,
      } = limitsAny;
      // if there are no external accounts on the organization and the user does not have permissions to add an external
      // account then ACH push limits should be 0
      if (
        limitsAny.ach_limits &&
        !externalAccounts.length &&
        !role.permissions.some(
          (p) => p.model_str === MODEL_STR.EXTERNAL_ACCOUNT,
        )
      ) {
        ach_limits = 0;
      }

      // if user does not have permissions to make ach payment, then unverified ach push limits should be 0
      if (
        limitsAny.ach_payment_limits &&
        !role.permissions.some(
          (p) =>
            p.model_str === MODEL_STR.ACH_PAYMENT ||
            p.model_str === MODEL_STR.ACH_PAYMENT_VIA_TEMPLATE ||
            p.model_str === MODEL_STR.ACH_PAYMENT_VIA_IMPORT,
        )
      ) {
        ach_payment_limits = 0;
      }

      // if user does not have permissions to collect ach payment, then unverified ach pull limits should be 0
      if (
        limitsAny.collect_ach_payment_limits &&
        !role.permissions.some(
          (p) =>
            p.model_str === MODEL_STR.COLLECT_ACH_PAYMENT ||
            p.model_str === MODEL_STR.ACH_PAYMENT_COLLECT_VIA_TEMPLATE ||
            p.model_str === MODEL_STR.ACH_PAYMENT_COLLECT_VIA_IMPORT,
        )
      ) {
        collect_ach_payment_limits = 0;
      }

      if (
        limitsAny.fednow_unverified_send_limits &&
        !role.permissions.some(
          (p) => p.model_str === MODEL_STR.UNVERIFIED_FEDNOW_SEND,
        )
      ) {
        fednow_unverified_send_limits = 0;
      }

      // if user does not have permissions to make international payment, then limits should be 0
      if (
        limitsAny.international_wires_limits &&
        !role.permissions.some(
          (p) =>
            p.model_str === MODEL_STR.INTERNATIONAL_WIRE ||
            p.model_str === MODEL_STR.INTERNATIONAL_WIRE_VIA_TEMPLATES,
        )
      ) {
        international_wire_limits = 0;
      }

      // if user does not have permission to send ACH pass-through files, then the send limit should be 0
      if (
        limitsAny.ach_passthrough_send_limits &&
        !role.permissions.some(
          (p) => p.model_str === MODEL_STR.ACH_PASSTHROUGH_SEND,
        )
      ) {
        ach_passthrough_send_limits = 0;
      }

      // if user does not have permission to collect ACH pass-through files, then the collect limit should be 0
      if (
        limitsAny.ach_passthrough_collect_limits &&
        !role.permissions.some(
          (p) => p.model_str === MODEL_STR.ACH_PASSTHROUGH_COLLECT,
        )
      ) {
        ach_passthrough_collect_limits = 0;
      }

      role.limits = {
        ach_push: { 1: ach_limits },
        wire: { 1: limitsAny.wire_limits },
        unverified_ach_push: { 1: ach_payment_limits },
        unverified_ach_pull: { 1: collect_ach_payment_limits },
        international_wire: { 1: international_wire_limits },
        fednow_unverified: { 1: fednow_unverified_send_limits },
        ach_passthrough_send: { 1: ach_passthrough_send_limits },
        ach_passthrough_collect: { 1: ach_passthrough_collect_limits },
      };
    }

    return role;
  }

  /** *
  Factory method returning a new instance of Role from
  an indigo.serialize'd Role
  ** */
  static deserialize(payload: any) {
    return new Role({ ...payload, uuid: payload.uuid || payload.id });
  }

  serialize() {
    return {
      uuid: this.uuid,
      organization_uuid: this.organization_uuid,
      name: this.name,
      description: this.description,
      permissions: this.permissions,
      limits: this.limits,
      deleted_at: this.deleted_at,
    };
  }

  /**
  True when per-account access is available, i.e. a detail response for an omni role or a
  converted role (including one with no accessible accounts). False for unconverted custom
  roles (`null`) and for roles loaded from the list (`undefined`).
  * */
  hasAccountPermissions(): boolean {
    return Array.isArray(this.account_permissions);
  }

  /** Per-account access for one account, or undefined if the role cannot access it. */
  getAccountPermission(accountUuid: string): API.AccountPermission | undefined {
    return this.account_permissions?.find(
      (entry) => entry.account_uuid === accountUuid,
    );
  }

  isDeleted(): boolean {
    return this.deleted_at !== null && this.deleted_at !== undefined;
  }

  // Used to decide which roles we want to allow users to be assigned to
  isAssignable(): boolean {
    return (
      !this.isDeleted() &&
      !this.isUnassigned() &&
      !this.isNoPermissions() &&
      !this.isAccountHolder()
    );
  }
}
